export const PRIORITY = {
  USER: 10,
  PREFETCH: 0,
};

export class ClientRequestQueue {
  constructor({ maxConcurrency = 2, maxPrefetchConcurrency = 1 } = {}) {
    this.maxConcurrency = maxConcurrency;
    this.maxPrefetchConcurrency = maxPrefetchConcurrency;
    this.activeTasks = new Map();
    this.waitingQueue = [];
    this.listeners = new Set();
  }

  on(event, listener) {
    if (event === "change" && typeof listener === "function") {
      this.listeners.add(listener);
    }
    return () => this.off(event, listener);
  }

  off(event, listener) {
    if (event === "change") {
      this.listeners.delete(listener);
    }
  }

  _emitChange() {
    const stats = this.getStats();
    for (const listener of this.listeners) {
      try {
        listener(stats);
      } catch (_e) {
        // Ignore listener errors
      }
    }
  }

  /**
   * Returns true if no USER priority request is currently active or waiting.
   * @returns {boolean}
   */
  isUserIdle() {
    for (const active of this.activeTasks.values()) {
      if (active.priority >= PRIORITY.USER) {
        return false;
      }
    }
    for (const waiting of this.waitingQueue) {
      if (waiting.priority >= PRIORITY.USER) {
        return false;
      }
    }
    return true;
  }

  _activePrefetchCount() {
    let count = 0;
    for (const active of this.activeTasks.values()) {
      if (active.priority < PRIORITY.USER) {
        count += 1;
      }
    }
    return count;
  }

  _abortWaitingPrefetches(exceptKey = null) {
    const kept = [];
    const aborted = [];
    for (const item of this.waitingQueue) {
      if (item.priority < PRIORITY.USER && item.key !== exceptKey) {
        aborted.push(item);
      } else {
        kept.push(item);
      }
    }
    this.waitingQueue = kept;
    for (const item of aborted) {
      const err = new Error(`Prefetch aborted for ${item.key}`);
      err.aborted = true;
      item.reject(err);
    }
  }

  _sortWaitingQueue() {
    this.waitingQueue.sort((a, b) => {
      if (b.priority !== a.priority) {
        return b.priority - a.priority;
      }
      return a.seq - b.seq;
    });
  }

  /**
   * Enqueue a task by key with dedupe and priority bumping.
   * @param {string} key
   * @param {Function} fn
   * @param {number} [priority=PRIORITY.USER]
   * @returns {Promise<*>}
   */
  enqueue(key, fn, priority = PRIORITY.USER) {
    // 1. Check if already active
    if (this.activeTasks.has(key)) {
      const active = this.activeTasks.get(key);
      if (priority > active.priority) {
        active.priority = priority;
      }
      if (priority >= PRIORITY.USER) {
        this._abortWaitingPrefetches(key);
      }
      this._emitChange();
      return active.promise;
    }

    // 2. Check if already waiting in queue
    const existingWaiting = this.waitingQueue.find((item) => item.key === key);
    if (existingWaiting) {
      if (priority > existingWaiting.priority) {
        existingWaiting.priority = priority;
        this._sortWaitingQueue();
      }
      if (priority >= PRIORITY.USER) {
        this._abortWaitingPrefetches(key);
      }
      this._emitChange();
      this._pump();
      return existingWaiting.promise;
    }

    // 3. New task
    if (priority >= PRIORITY.USER) {
      this._abortWaitingPrefetches();
    }

    let resolveRef;
    let rejectRef;
    const promise = new Promise((resolve, reject) => {
      resolveRef = resolve;
      rejectRef = reject;
    });

    this._seq = (this._seq || 0) + 1;
    const entry = {
      key,
      fn,
      priority,
      seq: this._seq,
      resolve: resolveRef,
      reject: rejectRef,
      promise,
    };

    this.waitingQueue.push(entry);
    this._sortWaitingQueue();
    this._emitChange();
    this._pump();

    return promise;
  }

  _pump() {
    while (
      this.waitingQueue.length > 0 &&
      this.activeTasks.size < this.maxConcurrency
    ) {
      let chosenIndex = -1;
      for (let i = 0; i < this.waitingQueue.length; i += 1) {
        const candidate = this.waitingQueue[i];
        if (candidate.priority >= PRIORITY.USER) {
          chosenIndex = i;
          break;
        }
        // Candidate is PREFETCH
        if (
          this.isUserIdle() &&
          this._activePrefetchCount() < this.maxPrefetchConcurrency
        ) {
          chosenIndex = i;
          break;
        }
      }

      if (chosenIndex === -1) {
        break;
      }

      const [nextItem] = this.waitingQueue.splice(chosenIndex, 1);
      const activeEntry = {
        key: nextItem.key,
        priority: nextItem.priority,
        promise: nextItem.promise,
      };
      this.activeTasks.set(nextItem.key, activeEntry);
      this._emitChange();

      Promise.resolve()
        .then(() => nextItem.fn({ priority: activeEntry.priority }))
        .then((result) => {
          this.activeTasks.delete(nextItem.key);
          nextItem.resolve(result);
          this._emitChange();
          this._pump();
        })
        .catch((err) => {
          this.activeTasks.delete(nextItem.key);
          nextItem.reject(err);
          this._emitChange();
          this._pump();
        });
    }
  }

  getStats() {
    let userWaiting = 0;
    let prefetchWaiting = 0;
    for (const item of this.waitingQueue) {
      if (item.priority >= PRIORITY.USER) {
        userWaiting += 1;
      } else {
        prefetchWaiting += 1;
      }
    }
    return {
      active: this.activeTasks.size,
      waiting: this.waitingQueue.length,
      byPriority: {
        user: userWaiting,
        prefetch: prefetchWaiting,
      },
      userIdle: this.isUserIdle(),
    };
  }
}

export const clientRequestQueue = new ClientRequestQueue();
export default clientRequestQueue;
